import {
  Button,
  Flex,
  Grid,
  Heading,
  IconButton,
  Typography,
} from "@sidelinesports/fan-design-system";
import { ArrowLeftIcon, Die3Icon } from "@sidelinesports/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link, useNavigate } from "react-router";
import { type ApiPlayerDetails, putPicksByGameId } from "../../api";
import { FullscreenFixedLayout } from "../../components";
import { PlayerInputRow } from "./components";
import { usePowerupPlayersForm } from "./hooks";

export type PowerupPlayersFormProps = {
  gameId: string;
  players: ApiPlayerDetails[];
  maxTokens: number;
  maxTokensPerPlayer: number;
};

export const PowerupPlayersForm = ({
  gameId,
  players,
  maxTokens,
  maxTokensPerPlayer,
}: PowerupPlayersFormProps) => {
  const playerIds = useMemo(
    () => players.map((player) => player.id),
    [players],
  );

  const navigate = useNavigate();

  const {
    canDistributeMore,
    getMaxTokensForPlayer,
    getTokensForPlayer,
    isFullyDistributed,
    isValid,
    playerTokens,
    randomizeTokens,
    setTokensForPlayer,
    tokensRemaining,
  } = usePowerupPlayersForm({
    playerIds,
    maxTokens,
    maxTokensPerPlayer,
  });

  const queryClient = useQueryClient();

  const submitPicksMutation = useMutation({
    // Transient failures (network blips, brief 5xx) self-heal via the ky client's
    // own retry (see api/client); the error only shows once those are exhausted.
    // React Query resets `isError` on the next `mutate()`, so the form stays
    // re-submittable and the message clears when the fan retries.
    mutationFn: async () => {
      if (!isValid || !isFullyDistributed) {
        throw new Error("Form is not valid or not fully distributed");
      }

      return putPicksByGameId(gameId, {
        picks: playerTokens.map((player) => ({
          playerId: player.id,
          tokens: player.tokens,
        })),
      });
    },
    onSuccess: async (result) => {
      // Optimistically update the picks in the cache
      queryClient.setQueryData(["games", gameId, "picks"], result);

      // Invalidate the picks query to ensure fresh data
      await queryClient.invalidateQueries({
        queryKey: ["games", gameId, "picks"],
      });

      navigate(`/games/${gameId}/play`, { replace: true });
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (submitPicksMutation.isPending || !isValid || !isFullyDistributed) {
          return;
        }
        submitPicksMutation.mutate();
      }}
    >
      <FullscreenFixedLayout
        header={<PowerupPlayersHeader />}
        footer={
          <PowerupYourPlayersFooter
            isValid={isValid}
            isSubmitting={submitPicksMutation.isPending}
            hasError={submitPicksMutation.isError}
            onRandomizePowerups={randomizeTokens}
            powerupsRemaining={tokensRemaining}
          />
        }
      >
        <Grid gap={24} px={24}>
          <Typography size="md">
            Add power-up points to your players. They'll earn more points each
            time they complete an event.
          </Typography>
          <Grid gap={16}>
            {players.map((player) => (
              <PlayerInputRow
                key={player.id}
                player={player}
                tokens={getTokensForPlayer(player.id)}
                maxTokens={getMaxTokensForPlayer(player.id)}
                maxOverallTokens={maxTokensPerPlayer}
                onChange={(tokens) => setTokensForPlayer(player.id, tokens)}
                isDisabled={
                  !canDistributeMore && getTokensForPlayer(player.id) === 0
                }
              />
            ))}
          </Grid>
        </Grid>
      </FullscreenFixedLayout>
    </form>
  );
};

const PowerupPlayersHeader = () => (
  <Flex align="center" gap={8} p={16}>
    <Link to={"../pick-your-players"}>
      <IconButton
        variant="outline-light"
        size="lg"
        icon={<ArrowLeftIcon />}
        aria-label="Go Back"
      />
    </Link>
    <Heading level={1} style={{ flexGrow: 1, textTransform: "uppercase" }}>
      Power Up Your Players
    </Heading>
  </Flex>
);

const PowerupYourPlayersFooter = ({
  isValid,
  isSubmitting,
  hasError,
  onRandomizePowerups,
  powerupsRemaining,
}: {
  isValid: boolean;
  isSubmitting: boolean;
  hasError: boolean;
  onRandomizePowerups?: () => void;
  powerupsRemaining: number;
}) => (
  <Flex direction="column" justify="center" gap={16}>
    <Flex direction="column" justify="center" align="center" gap={0}>
      <Heading as="div" level={2}>
        {powerupsRemaining ?? 0}
      </Heading>
      <Typography size="md" color="muted">
        power-ups remaining
      </Typography>
    </Flex>
    {hasError && (
      <Typography
        role="alert"
        size="md"
        color="readable"
        textAlign="center"
        px={16}
      >
        Couldn't submit your picks. Please check your connection and tap
        Continue to try again.
      </Typography>
    )}
    <Flex
      justify="center"
      p={16}
      gap={16}
      style={{ boxShadow: "0px -4px 16px 0px rgba(0, 0, 0, 0.40)" }}
    >
      <IconButton
        type="button"
        variant="outline-light"
        size="lg"
        icon={<Die3Icon />}
        aria-label="Randomize Players"
        style={{ stroke: "currentColor", flexShrink: 0, flexGrow: 0 }}
        onClick={onRandomizePowerups}
      />
      <Button
        type="submit"
        variant="action"
        size="sm"
        style={{ flexGrow: 1 }}
        disabled={!isValid}
        loading={isSubmitting}
      >
        Continue
      </Button>
    </Flex>
  </Flex>
);
